import { useContext, useMemo } from 'react';
import { OptimizelyContext, useDecision } from '@optimizely/react-sdk';
import { OptimizelyDecideOption } from '@optimizely/optimizely-sdk';
import { TL709_ROOMS_RECOMMENDED_FOR_YOU_FLAG_KEY } from '../../experiment-registry';
import { isExperimentRuleDecision } from './is-experiment-rule';

/** Fallbacks matching the flag configuration. */
const ROOMS_RECOMMENDED_FOR_YOU_DEFAULTS = {
	threshold_one: 5,
	threshold_two: 8,
	max_recommendations: 3,
};

export type RoomsRecommendedForYouVariables = typeof ROOMS_RECOMMENDED_FOR_YOU_DEFAULTS;

/** Structural, since the core and React SDK decision types are not assignable. */
type DecisionVariables = { variables?: { [key: string]: unknown } };

// Module scope on purpose: a new array per render makes useDecision re-subscribe in a loop.
const SHADOW_DECIDE_OPTIONS = [OptimizelyDecideOption.DISABLE_DECISION_EVENT];

const REAL_DECIDE_OPTIONS: OptimizelyDecideOption[] = [];

const SHADOW_OPTIONS = { autoUpdate: true, decideOptions: SHADOW_DECIDE_OPTIONS };

// No autoUpdate, so the impression is sent once per mount.
const REAL_OPTIONS = { autoUpdate: false, decideOptions: REAL_DECIDE_OPTIONS };

const toNumber = (value: unknown, fallback: number): number =>
	typeof value === 'number' && Number.isFinite(value) ? value : fallback;

// Memoised on the primitives so the object keeps its identity across renders.
function useFlagVariables(
	decision: DecisionVariables | undefined
): RoomsRecommendedForYouVariables {
	const thresholdOne = decision?.variables?.['threshold_one'];
	const thresholdTwo = decision?.variables?.['threshold_two'];
	const maxRecommendations = decision?.variables?.['max_recommendations'];

	return useMemo(
		() => ({
			threshold_one: toNumber(thresholdOne, ROOMS_RECOMMENDED_FOR_YOU_DEFAULTS.threshold_one),
			threshold_two: toNumber(thresholdTwo, ROOMS_RECOMMENDED_FOR_YOU_DEFAULTS.threshold_two),
			max_recommendations: toNumber(
				maxRecommendations,
				ROOMS_RECOMMENDED_FOR_YOU_DEFAULTS.max_recommendations
			),
		}),
		[thresholdOne, thresholdTwo, maxRecommendations]
	);
}

// Both hooks call hooks after an early return, like the other experiment hooks. It holds
// because the provider remounts the subtree once consent resolves.

/**
 * Shadow decision, no impression. Also returns the flag variables, which both arms share and
 * which are needed before the real decision mounts.
 */
export function useRoomsRecommendedForYouAudience(): {
	inAudience: boolean;
	variables: RoomsRecommendedForYouVariables;
} {
	const { optimizely } = useContext(OptimizelyContext);

	if (!optimizely) {
		return { inAudience: false, variables: ROOMS_RECOMMENDED_FOR_YOU_DEFAULTS };
	}

	const [decision, clientReady] = useDecision(
		TL709_ROOMS_RECOMMENDED_FOR_YOU_FLAG_KEY,
		SHADOW_OPTIONS
	);

	const variables = useFlagVariables(decision);

	const inAudience =
		clientReady &&
		isExperimentRuleDecision(
			TL709_ROOMS_RECOMMENDED_FOR_YOU_FLAG_KEY,
			decision?.ruleKey,
			optimizely.getOptimizelyConfig()
		);

	return { inAudience, variables };
}

/** Real decision. Emits the impression, so mount it only once the section would render. */
export function useRoomsRecommendedForYou(): { enabled: boolean } {
	const { optimizely } = useContext(OptimizelyContext);

	if (!optimizely) {
		return { enabled: false };
	}

	const [decision, clientReady] = useDecision(
		TL709_ROOMS_RECOMMENDED_FOR_YOU_FLAG_KEY,
		REAL_OPTIONS
	);

	return { enabled: clientReady && (decision?.enabled ?? false) };
}
